// Per-action input forms for the consult panel (nsofar docs/33 §3).
// Pure controlled forms — submission, error surfacing, and the source-of-truth
// rule live in ConsultPanel. Split from the panel for the component-size limit.
//
// Shortlist rows offer the doctors-mirror picker (docs/33 §4 amendment): pick
// a known doctor (sends doctor_id) or enter a new one inline (nsofar upserts
// into its registry and the doctor becomes pickable after the next sync).

import { useState, type JSX } from "react";
import type { ConsultDoctorSummary } from "@hcm/core";

const FIELD = "w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm";
const LABEL = "block text-xs text-slate-500 mb-1";
const BTN_PRIMARY = "bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 transition-colors disabled:opacity-50";

// ── Shortlist (1–3 options; inline doctor XOR existing doctor_id) ────────────

export interface ShortlistOptionInput {
  doctor_id?: string;
  doctor?: {
    full_name: string; specialty: string;
    qualifications?: string[]; registration_no?: string; registration_council?: string;
    years_experience?: number; languages?: string[];
  };
  datetime_ist: string;
}

interface OptionRow {
  known_id: string; // nsofar_doctor_id from the mirror picker; "" = enter a new doctor inline
  full_name: string; specialty: string; qualifications: string; registration_no: string;
  registration_council: string; years_experience: string; languages: string;
  datetime_ist: string;
}

const EMPTY_ROW: OptionRow = {
  known_id: "",
  full_name: "", specialty: "", qualifications: "", registration_no: "",
  registration_council: "", years_experience: "", languages: "", datetime_ist: "",
};

function csv(s: string): string[] | undefined {
  const items = s.split(",").map((x) => x.trim()).filter(Boolean);
  return items.length ? items : undefined;
}

function rowToOption(r: OptionRow): ShortlistOptionInput {
  if (r.known_id) return { doctor_id: r.known_id, datetime_ist: r.datetime_ist };
  return {
    doctor: {
      full_name: r.full_name.trim(), specialty: r.specialty.trim(),
      qualifications: csv(r.qualifications), registration_no: r.registration_no.trim() || undefined,
      registration_council: r.registration_council.trim() || undefined,
      years_experience: r.years_experience ? Number(r.years_experience) : undefined,
      languages: csv(r.languages),
    },
    datetime_ist: r.datetime_ist,
  };
}

function rowValid(r: OptionRow): boolean {
  if (!r.datetime_ist) return false;
  return r.known_id !== "" || (r.full_name.trim().length >= 2 && r.specialty.trim().length >= 2);
}

function doctorLabel(d: ConsultDoctorSummary): string {
  const reg = [d.registration_no, d.registration_council].filter(Boolean).join(" ");
  return `${d.full_name} — ${d.specialty || "—"}${reg ? ` · ${reg}` : ""}`;
}

function DoctorPicker({ value, doctors, onChange }: {
  value: string; doctors: ConsultDoctorSummary[]; onChange: (id: string) => void;
}): JSX.Element {
  const picked = doctors.find((d) => d.nsofar_doctor_id === value);
  return (
    <div>
      <span className={LABEL}>Doctor</span>
      <select className={FIELD} value={value} onChange={(e) => onChange(e.target.value)} aria-label="Doctor">
        <option value="">New doctor — enter details below…</option>
        {doctors.map((d) => (
          <option key={d.nsofar_doctor_id} value={d.nsofar_doctor_id}>{doctorLabel(d)}</option>
        ))}
      </select>
      {picked && (
        <p className="text-[11px] text-slate-400 mt-0.5">
          {[picked.qualifications.join(", "), picked.languages.join(", ")].filter(Boolean).join(" · ") || "Known doctor — details on file with nsofar."}
        </p>
      )}
      {doctors.length === 0 && (
        <p className="text-[11px] text-slate-400 mt-0.5">
          No known doctors yet — every doctor shortlisted once becomes pickable here after the next sync.
        </p>
      )}
    </div>
  );
}

function NewDoctorFields({ row, set }: { row: OptionRow; set: (patch: Partial<OptionRow>) => void }): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div><span className={LABEL}>Full name *</span>
        <input className={FIELD} value={row.full_name} onChange={(e) => set({ full_name: e.target.value })} placeholder="Dr. Anjali Rao" /></div>
      <div><span className={LABEL}>Specialty *</span>
        <input className={FIELD} value={row.specialty} onChange={(e) => set({ specialty: e.target.value })} placeholder="General physician" /></div>
      <div><span className={LABEL}>Qualifications (comma-sep)</span>
        <input className={FIELD} value={row.qualifications} onChange={(e) => set({ qualifications: e.target.value })} placeholder="MBBS, MD" /></div>
      <div><span className={LABEL}>Registration no.</span>
        <input className={FIELD} value={row.registration_no} onChange={(e) => set({ registration_no: e.target.value })} /></div>
      <div><span className={LABEL}>Registration council</span>
        <input className={FIELD} value={row.registration_council} onChange={(e) => set({ registration_council: e.target.value })} /></div>
      <div><span className={LABEL}>Years experience</span>
        <input className={FIELD} type="number" min={0} max={80} value={row.years_experience} onChange={(e) => set({ years_experience: e.target.value })} /></div>
      <div className="col-span-2"><span className={LABEL}>Languages (comma-sep)</span>
        <input className={FIELD} value={row.languages} onChange={(e) => set({ languages: e.target.value })} placeholder="Hindi, English" /></div>
    </div>
  );
}

function OptionRowFields({ row, doctors, onChange }: {
  row: OptionRow; doctors: ConsultDoctorSummary[]; onChange: (r: OptionRow) => void;
}): JSX.Element {
  const set = (patch: Partial<OptionRow>): void => onChange({ ...row, ...patch });
  return (
    <div className="space-y-2">
      <DoctorPicker value={row.known_id} doctors={doctors} onChange={(id) => set({ known_id: id })} />
      {row.known_id === "" && <NewDoctorFields row={row} set={set} />}
      <div><span className={LABEL}>Slot (IST) *</span>
        <input className={FIELD} type="datetime-local" value={row.datetime_ist} onChange={(e) => set({ datetime_ist: e.target.value })} />
        <p className="text-[11px] text-slate-400 mt-0.5">Enter the time as IST — it is sent verbatim as the doctor&apos;s slot.</p></div>
    </div>
  );
}

export function ShortlistForm({ onSubmit, pending, doctors }: {
  onSubmit: (options: ShortlistOptionInput[]) => void; pending: boolean; doctors: ConsultDoctorSummary[];
}): JSX.Element {
  const [rows, setRows] = useState<OptionRow[]>([{ ...EMPTY_ROW }]);
  const valid = rows.length >= 1 && rows.length <= 3 && rows.every(rowValid);
  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">Option {i + 1}</span>
            {rows.length > 1 && (
              <button type="button" className="text-xs text-red-600 hover:underline"
                onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
            )}
          </div>
          <OptionRowFields row={row} doctors={doctors} onChange={(r) => setRows(rows.map((x, j) => (j === i ? r : x)))} />
        </div>
      ))}
      <div className="flex items-center gap-3">
        {rows.length < 3 && (
          <button type="button" className="text-sm text-navy-800 hover:underline"
            onClick={() => setRows([...rows, { ...EMPTY_ROW }])}>+ Add option</button>
        )}
        <button type="button" className={BTN_PRIMARY} disabled={!valid || pending}
          onClick={() => onSubmit(rows.map(rowToOption))}>
          {pending ? "Sending…" : `Send shortlist (${rows.length})`}
        </button>
      </div>
    </div>
  );
}

// ── Confirm booking ───────────────────────────────────────────────────────────

export interface ConfirmInput {
  confirmed_datetime_ist: string;
  join_link?: string; callback_note?: string;
  partner_booking_ref?: string; partner_cost_minor?: number;
}

export function ConfirmForm({ onSubmit, pending }: { onSubmit: (body: ConfirmInput) => void; pending: boolean }): JSX.Element {
  const [f, setF] = useState({ confirmed_datetime_ist: "", join_link: "", callback_note: "", partner_booking_ref: "", partner_cost_minor: "" });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="col-span-2"><span className={LABEL}>Confirmed slot (IST) *</span>
        <input className={FIELD} type="datetime-local" value={f.confirmed_datetime_ist} onChange={set("confirmed_datetime_ist")} /></div>
      <div className="col-span-2"><span className={LABEL}>Video join link (optional — can be relayed later)</span>
        <input className={FIELD} value={f.join_link} onChange={set("join_link")} placeholder="https://…" /></div>
      <div className="col-span-2"><span className={LABEL}>Callback note (audio fallback)</span>
        <input className={FIELD} value={f.callback_note} onChange={set("callback_note")} placeholder="Doctor will call the parent at…" /></div>
      <div><span className={LABEL}>Partner booking ref (internal)</span>
        <input className={FIELD} value={f.partner_booking_ref} onChange={set("partner_booking_ref")} /></div>
      <div><span className={LABEL}>Partner cost, minor units (internal)</span>
        <input className={FIELD} type="number" min={0} value={f.partner_cost_minor} onChange={set("partner_cost_minor")} placeholder="e.g. 50000 = ₹500" /></div>
      <div className="col-span-2">
        <button type="button" className={BTN_PRIMARY} disabled={!f.confirmed_datetime_ist || pending}
          onClick={() => onSubmit({
            confirmed_datetime_ist: f.confirmed_datetime_ist,
            join_link: f.join_link.trim() || undefined,
            callback_note: f.callback_note.trim() || undefined,
            partner_booking_ref: f.partner_booking_ref.trim() || undefined,
            partner_cost_minor: f.partner_cost_minor ? Number(f.partner_cost_minor) : undefined,
          })}>
          {pending ? "Sending…" : "Confirm booking"}
        </button>
      </div>
    </div>
  );
}

// ── Single-field forms ────────────────────────────────────────────────────────

export function SingleFieldForm({ label, placeholder, required, buttonLabel, pending, onSubmit }: {
  label: string; placeholder?: string; required: boolean; buttonLabel: string;
  pending: boolean; onSubmit: (value: string) => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  return (
    <div className="space-y-2">
      <div><span className={LABEL}>{label}</span>
        <input className={FIELD} value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} /></div>
      <button type="button" className={BTN_PRIMARY} disabled={pending || (required && !value.trim())}
        onClick={() => onSubmit(value.trim())}>
        {pending ? "Sending…" : buttonLabel}
      </button>
    </div>
  );
}
